(() => {
  if (customElements.get('md-daniverse')) return;
  const safeWeights = (value, fallback = {}) => {
    try {
      const object = typeof value === 'string' ? JSON.parse(value) : value;
      if (!object || typeof object !== 'object' || Array.isArray(object)) return fallback;
      return Object.fromEntries(Object.entries(object).filter(([key, weight]) => /^[a-z_]+$/.test(key) && Number.isFinite(Number(weight))).map(([key, weight]) => [key, Number(weight)]));
    } catch { return fallback; }
  };
  const safeUrl = (value) => {
    if (!value) return '';
    try { const url = new URL(value, location.origin); return /^https?:$/.test(url.protocol) ? url.href : ''; } catch { return ''; }
  };
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  class Daniverse extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.panel = this.querySelector('.md-dv__desktop');
      this.veil = node('div', 'md-dv__veil'); this.veil.hidden = true; this.prepend(this.veil);
      this.dialog = this.querySelector('dialog');
      this.oracle = this.querySelector('.md-oracle');
      this.config = JSON.parse(this.querySelector('[data-md-oracle-config]').textContent);
      this.questions = structuredClone(window.MDOracle.data.questions);
      this.recommendations = [];
      this.triggers = new Map();
      this.answers = {};
      this.step = 0;
      this.pinned = false; this.pageMode = false;
      this.applyConfig();
      this.storageKey = `md-oracle-v${window.MDOracle.data.version}-${this.dataset.sectionId}`;
      this.restore();
      this.on(document, 'click', e => this.handleClick(e), true);
      this.on(document, 'keydown', e => this.handleKey(e));
      this.on(document, 'focusin', e => {
        if (!this.panel.hidden && !this.panel.contains(e.target) && !this.triggers.has(e.target.closest('.md-dv-trigger'))) this.closeMenu();
      });
      this.on(window, 'resize', () => { this.closeMenu(); });
      this.on(window, 'scroll', () => { if (!this.panel.hidden) this.position(); }, { passive: true });
      this.on(window, 'hashchange', () => this.checkHash());
      this.on(this.panel, 'pointerenter', () => clearTimeout(this.closeTimer));
      this.on(this.panel, 'pointerleave', () => this.queueClose());
      this.on(this.dialog, 'cancel', e => { e.preventDefault(); this.exitOracle(); });
      this.on(this.oracle, 'change', e => {
        if (!e.target.matches('[data-md-answer]')) return;
        this.answers[this.questions[this.step].id] = e.target.value;
        this.oracle.querySelector('[data-md-next]').disabled = false;
        this.persist();
      });
      this.bindDesktop();
      const initialize = () => { this.mountMobile(); this.mountPage(); this.ensureSampleAnchor(); this.checkHash(); };
      if (document.readyState === 'loading') this.on(document, 'DOMContentLoaded', initialize, { once: true }); else initialize();
      // Ella replaces navigation after lazy loading and the theme editor rerenders sections.
      this.observer = new MutationObserver(() => this.bindDesktop());
      const header = document.querySelector('#HeaderNavigation')?.closest('.shopify-section') || document.querySelector('.section-header-navigation');
      if (header) this.observer.observe(header, { childList: true, subtree: true });
      this.on(document, 'shopify:section:load', () => { this.bindDesktop(); this.mountMobile(); this.ensureSampleAnchor(); });
      this.on(document, 'shopify:block:select', e => {
        const block = this.config.overrides.find(b => b.id === e.detail?.blockId);
        if (!block) return;
        if (['question', 'answer', 'recommendation'].includes(block.type)) {
          this.openOracle();
          const index = this.questions.findIndex(q => q.id === block.settings.question_id || q.answers.some(a => a.id === block.settings.answer_id));
          if (index >= 0) this.step = index;
          this.render(true);
        } else if (window.innerWidth > 1024) this.openMenu(this.triggers.keys().next().value);
      });
    }
    on(target, event, callback, options = {}) {
      target.addEventListener(event, callback, typeof options === 'boolean' ? { capture: options, signal: this.signal } : { ...options, signal: this.signal });
    }
    applyConfig() {
      this.config.overrides.forEach(block => {
        const s = block.settings;
        if (block.type === 'question') {
          const q = this.questions.find(q => q.id === s.question_id);
          if (q) { q.title = s.title || q.title; q.helper = s.helper || q.helper; }
        }
        if (block.type === 'answer') {
          const a = this.questions.flatMap(q => q.answers).find(a => a.id === s.answer_id);
          if (a) { a.label = s.label || a.label; a.image = block.image; a.signals = safeWeights(s.signals, a.signals); }
        }
        if (block.type === 'recommendation' && s.title && safeUrl(s.destination)) this.recommendations.push({
          id: block.id, title: s.title, copy: s.copy, destination: safeUrl(s.destination), sample: safeUrl(s.sample), purchase: safeUrl(s.purchase), image: block.image, art: s.art, weights: safeWeights(s.weights)
        });
      });
    }
    bindDesktop() {
      document.querySelectorAll('#HeaderNavigation .menu-lv-1 > a.menu-lv-1__action').forEach(link => {
        const title = link.querySelector('.text')?.textContent.trim().toLowerCase();
        if (title !== this.dataset.trigger.trim().toLowerCase()) return;
        const trigger = node('button', `${link.className} md-dv-trigger`);
        trigger.type = 'button';
        trigger.innerHTML = link.innerHTML;
        trigger.setAttribute('aria-expanded', 'false');
        trigger.setAttribute('aria-controls', this.panel.id);
        if (!trigger.querySelector('.icon-dropdown')) trigger.append(node('span', 'icon-dropdown', '›'));
        const host = link.parentElement;
        host.classList.add('md-dv-host');
        this.triggers.set(trigger, link);
        link.replaceWith(trigger);
        this.on(host, 'pointerenter', e => { if (e.pointerType === 'mouse') this.openMenu(trigger); });
        this.on(host, 'pointerleave', () => this.queueClose());
        this.on(trigger, 'focus', () => { if (!this.suppressFocus && trigger.matches(':focus-visible')) this.openMenu(trigger); });
      });
    }
    mountMobile() {
      if (this.mobile?.isConnected) return;
      const container = document.querySelector('#navigation-mobile [data-navigation-mobile]');
      if (!container) return;
      this.mobile = node('details', 'md-dv__mobile');
      this.mobile.setAttribute('style', this.getAttribute('style'));
      const summary = node('summary', '', 'Shop · Enter the Daniverse');
      summary.setAttribute('aria-controls', `${this.panel.id}-mobile`);
      summary.setAttribute('aria-expanded', 'false');
      const content = this.panel.firstElementChild.cloneNode(true);
      content.id = `${this.panel.id}-mobile`;
      content.querySelectorAll('[data-shopify-editor-block]').forEach(el => el.removeAttribute('data-shopify-editor-block'));
      this.mobile.append(summary, content);
      container.before(this.mobile);
      const luxeNav = document.querySelector('#HeaderNavigation .md-luxe-nav');
      if (luxeNav) {
        this.mobileNav = node('nav');
        this.mobileNav.setAttribute('aria-label', 'Main navigation');
        const links = luxeNav.cloneNode(true);
        links.querySelector('.md-luxe-shop')?.remove();
        links.querySelectorAll('details').forEach(details => { details.open = false; });
        this.mobileNav.append(links);
        this.mobile.after(this.mobileNav);
        this.originalMobileNav = container;
        container.classList.add('md-luxe-mobile-original');
      }
      this.on(this.mobile, 'toggle', () => summary.setAttribute('aria-expanded', String(this.mobile.open)));
    }
    mountPage() {
      const page = document.querySelector('[data-md-oracle-page]');
      if (!page) return;
      this.pageMode = true;
      this.page = page; this.pageOriginal = [...page.childNodes];
      page.setAttribute('style', this.getAttribute('style'));
      page.replaceChildren(this.oracle);
      this.render(false);
    }
    ensureSampleAnchor() {
      const sampler = document.querySelector('[id^="MDSV2-"]');
      if (sampler && !document.getElementById('md-sample-vault')) {
        const anchor = node('span'); anchor.id = 'md-sample-vault'; anchor.style.cssText = 'display:block;height:0;scroll-margin-top:112px';
        sampler.prepend(anchor); this.sampleAnchor = anchor;
      }
      if (location.hash === '#md-sample-vault') this.scrollToSample();
    }
    scrollToSample() {
      const sampler = document.querySelector('[id^="MDSV2-"]');
      if (!sampler) return false;
      (document.getElementById('md-sample-vault') || sampler).scrollIntoView({ behavior: 'instant', block: 'start' });
      return true;
    }
    handleClick(e) {
      document.querySelectorAll('.md-luxe-series details[open]').forEach(details => {
        if (!details.contains(e.target)) details.open = false;
      });
      const trigger = e.target.closest('.md-dv-trigger');
      if (this.triggers.has(trigger)) {
        e.preventDefault(); e.stopPropagation();
        if (!this.panel.hidden && this.pinned) this.closeMenu(); else { this.openMenu(trigger); this.pinned = true; }
        return;
      }
      const opener = e.target.closest('[data-md-oracle-open]');
      if (opener) {
        e.preventDefault(); e.stopPropagation(); this.openOracle(opener.dataset.mdPoison, opener); return;
      }
      if (e.target.closest('[data-md-oracle-exit]')) { this.exitOracle(); return; }
      if (e.target.closest('[data-md-back]')) { this.step = Math.max(0, this.step - 1); this.render(true); this.persist(); return; }
      if (e.target.closest('[data-md-next]')) {
        if (this.step === this.questions.length) { this.answers = {}; this.step = 0; }
        else if (this.answers[this.questions[this.step].id]) this.step++;
        this.render(true); this.persist(); return;
      }
      const sample = e.target.closest('a');
      if (sample) {
        const url = new URL(sample.href, location.origin);
        if (url.origin === location.origin && url.hash === '#md-sample-vault' && document.querySelector('[id^="MDSV2-"]')) {
          e.preventDefault(); this.closeMenu(); this.exitOracle(false); this.closeMobile(); this.scrollToSample();
          const title = document.querySelector('[id^="MDSV2-"] h2');
          if (title) { title.tabIndex = -1; title.focus({ preventScroll: true }); }
          history.replaceState(null, '', '#md-sample-vault'); return;
        }
      }
      if (!this.panel.hidden && !this.panel.contains(e.target)) this.closeMenu();
    }
    handleKey(e) {
      const series = e.target.closest('.md-luxe-series details[open]');
      if (e.key === 'Escape' && series) { e.preventDefault(); series.open = false; series.querySelector('summary').focus(); return; }
      if (e.key === 'Tab' && !this.panel.hidden) {
        const links = [...this.panel.querySelectorAll('a[href]')];
        if (e.target === this.activeTrigger && !e.shiftKey) { e.preventDefault(); links[0]?.focus(); }
        else if (e.target === links[0] && e.shiftKey) { e.preventDefault(); this.closeMenu(true); }
        else if (e.target === links.at(-1) && !e.shiftKey) {
          e.preventDefault();
          const following = this.activeTrigger?.closest('li')?.nextElementSibling?.querySelector('summary,a,button');
          this.closeMenu(); (following || this.activeTrigger)?.focus();
        }
      }
      if (e.key === 'Escape' && !this.panel.hidden) { e.preventDefault(); e.stopPropagation(); this.closeMenu(true); }
      if (e.key === 'ArrowDown' && this.triggers.has(e.target)) { e.preventDefault(); this.openMenu(e.target); this.panel.querySelector('a')?.focus(); }
      if (e.key === 'Escape' && this.mobile?.open && document.body.classList.contains('menu_open')) { this.mobile.open = false; this.mobile.querySelector('summary').focus(); }
    }
    openMenu(trigger) {
      if (window.innerWidth <= 1024 || !trigger?.isConnected) return;
      clearTimeout(this.closeTimer);
      document.querySelectorAll('.md-luxe-series details[open]').forEach(details => { details.open = false; });
      this.activeTrigger = trigger;
      this.panel.hidden = false;
      this.veil.hidden = false; document.body.classList.add('md-dv-menu-open');
      this.triggers.forEach((_, button) => button.setAttribute('aria-expanded', String(button === trigger)));
      this.position();
    }
    position() {
      const header = this.activeTrigger?.closest('.header-navigation');
      if (!header || !this.activeTrigger?.isConnected) { this.closeMenu(); return; }
      const bottom = Math.max(0, header.getBoundingClientRect().bottom);
      this.panel.style.setProperty('--md-dv-top', `${Math.round(bottom)}px`);
      this.veil.style.setProperty('--md-dv-top', `${Math.round(bottom)}px`);
      const rect = this.panel.getBoundingClientRect(), trigger = this.activeTrigger.getBoundingClientRect();
      this.panel.style.setProperty('--md-dv-notch', `${Math.max(20, Math.min(rect.width - 30, trigger.left + trigger.width / 2 - rect.left - 7))}px`);
    }
    queueClose() {
      if (this.pinned) return;
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => {
        if (!this.panel.matches(':hover') && !this.activeTrigger?.parentElement.matches(':hover') && !this.panel.contains(document.activeElement)) this.closeMenu();
      }, 230);
    }
    closeMenu(returnFocus = false) {
      this.panel.hidden = true; this.pinned = false;
      this.veil.hidden = true; document.body.classList.remove('md-dv-menu-open');
      clearTimeout(this.closeTimer);
      this.triggers.forEach((_, trigger) => trigger.setAttribute('aria-expanded', 'false'));
      if (returnFocus) { this.suppressFocus = true; this.activeTrigger?.focus(); this.suppressFocus = false; }
    }
    closeMobile() {
      if (document.body.classList.contains('menu_open')) document.querySelector('#navigation-mobile [data-menu-close-sidebar]')?.click();
    }
    openOracle(poison, opener) {
      this.returnTarget = opener || document.activeElement;
      if (poison && this.questions[0].answers.some(a => a.id === poison)) { this.answers[this.questions[0].id] = poison; this.step = 0; }
      this.closeMenu(); this.closeMobile();
      this.render(false);
      if (!this.pageMode && !this.dialog.open) { this.dialog.showModal(); document.body.classList.add('md-oracle-open'); }
      if (this.pageMode) this.oracle.scrollIntoView({ block: 'start' });
      this.oracle.querySelector('[data-md-question-title]')?.focus({ preventScroll: true });
      if (this.dialog.open) this.dialog.scrollTop = 0;
      this.persist();
    }
    exitOracle(restoreFocus = true) {
      if (this.dialog.open) this.dialog.close();
      document.body.classList.remove('md-oracle-open');
      document.body.classList.remove('md-dv-menu-open');
      if (location.hash === '#obsession-oracle') history.replaceState(null, '', location.pathname + location.search);
      if (restoreFocus) {
        const target = this.returnTarget;
        this.suppressFocus = true;
        if (target?.isConnected && target.getClientRects().length && !target.closest('[hidden]')) target.focus();
        else if (window.innerWidth <= 1024) document.querySelector('.mobileMenu-toggle')?.focus();
        else this.activeTrigger?.focus();
        this.suppressFocus = false;
      }
      if (this.pageMode && restoreFocus) location.assign(this.config.allUrl);
    }
    checkHash() { if (location.hash === '#obsession-oracle') this.openOracle(); else if (location.hash === '#md-sample-vault') this.scrollToSample(); }
    render(moveFocus) {
      const stage = this.oracle.querySelector('[data-md-stage]');
      const progress = this.oracle.querySelector('[data-md-progress]');
      stage.replaceChildren(); progress.replaceChildren();
      [...this.questions.map(q => q.label), 'Your match'].forEach((label, i) => {
        const li = node('li', i < this.step ? 'is-complete' : '');
        if (i === this.step) li.setAttribute('aria-current', 'step');
        li.append(node('span', '', i === this.questions.length ? '♡' : i < this.step ? '✓' : String(i + 1)), document.createTextNode(label)); progress.append(li);
      });
      const next = this.oracle.querySelector('[data-md-next]');
      const back = this.oracle.querySelector('[data-md-back]');
      back.hidden = this.step === 0;
      if (this.step < this.questions.length) {
        const q = this.questions[this.step];
        const title = node('h2', 'md-oracle__question', q.title); title.id = `MDQuestion-${this.dataset.sectionId}`; title.tabIndex = -1; title.dataset.mdQuestionTitle = '';
        const answers = node('fieldset', 'md-oracle__answers'); answers.setAttribute('aria-labelledby', title.id);
        q.answers.forEach(a => {
          const label = node('label', 'md-oracle__answer');
          const input = node('input'); input.type = 'radio'; input.name = `md-oracle-${this.dataset.sectionId}-${q.id}`; input.value = a.id; input.dataset.mdAnswer = ''; input.checked = this.answers[q.id] === a.id;
          label.append(input, this.art(a), node('strong', '', a.label));
          const check = node('span', 'md-oracle__check', '✓'); check.setAttribute('aria-hidden', 'true'); label.append(check); answers.append(label);
        });
        stage.append(title, answers);
        next.replaceChildren(document.createTextNode(this.step === this.questions.length - 1 ? this.config.resultLabel : this.config.nextLabel), node('span', '', '⟶'));
        next.disabled = !this.answers[q.id];
        this.oracle.querySelector('[data-md-helper]').textContent = q.helper;
      } else {
        this.renderResult(stage);
        next.textContent = 'Consult the Oracle again'; next.disabled = false;
        this.oracle.querySelector('[data-md-helper]').textContent = 'Your next obsession is only a page away.';
      }
      if (moveFocus) {
        this.oracle.querySelector('[data-md-question-title]')?.focus({ preventScroll: true });
        if (this.dialog.open) this.dialog.scrollTop = 0;
        else this.oracle.scrollIntoView({ block: 'start', behavior: 'instant' });
      }
    }
    art(item) {
      if (item.image && safeUrl(item.image)) {
        const image = node('img', 'md-dv__image'); image.src = item.image; image.alt = ''; image.loading = 'lazy'; image.width = 600; image.height = 340; return image;
      }
      const art = node('span', `md-dv__art md-dv__art--${item.art || 'fate'}`); art.setAttribute('aria-hidden', 'true'); return art;
    }
    renderResult(stage) {
      const result = window.MDOracle.score(this.questions, this.answers, this.recommendations);
      const card = node('div', 'md-oracle__result');
      const art = node('div', 'md-oracle__result-art'); art.append(this.art(result || { art: 'fate' }));
      const copy = node('div'); copy.append(node('span', 'md-dv__eyebrow', this.config.provisional ? 'A first temptation' : 'Your match'));
      const title = node('h2', '', result?.title || 'Your next obsession awaits'); title.tabIndex = -1; title.dataset.mdQuestionTitle = '';
      copy.append(title, node('p', '', result?.copy || 'Explore the collection and discover your next story.'));
      const primary = node('a', 'md-oracle__cta', this.answers.commitment === 'taste' && result?.sample ? 'Taste the temptation →' : 'Explore this story →');
      primary.href = this.answers.commitment === 'taste' && result?.sample ? result.sample : result?.destination || this.config.allUrl;
      copy.append(primary);
      const links = node('div', 'md-oracle__result-links');
      if (result?.sample && primary.href !== result.sample) { const sample = node('a', '', 'Read or listen to a sample'); sample.href = result.sample; links.append(sample); }
      if (result?.purchase) { const purchase = node('a', '', 'Claim your obsession'); purchase.href = result.purchase; links.append(purchase); }
      const all = node('a', '', 'Explore all books'); all.href = this.config.allUrl; links.append(all); copy.append(links);
      if (this.config.provisional) copy.append(node('p', 'md-oracle__notice', this.config.notice));
      card.append(art, copy); stage.append(card);
      this.oracle.querySelector('[data-md-announcement]').textContent = `Your result: ${result?.title || 'Explore the collection'}.`;
    }
    restore() {
      if (!this.config.remember) return;
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
        if (!saved) return;
        this.questions.forEach(q => { if (q.answers.some(a => a.id === saved.answers?.[q.id])) this.answers[q.id] = saved.answers[q.id]; });
        const firstMissing = this.questions.findIndex(q => !this.answers[q.id]);
        const maximum = firstMissing === -1 ? this.questions.length : firstMissing;
        this.step = Math.min(Math.max(0, Number(saved.step) || 0), maximum);
      } catch { /* Storage may be unavailable in private browsing. */ }
    }
    persist() {
      if (!this.config.remember) return;
      try { sessionStorage.setItem(this.storageKey, JSON.stringify({ answers: this.answers, step: this.step })); } catch { /* Answers remain usable in memory. */ }
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.observer?.disconnect(); clearTimeout(this.closeTimer);
      this.triggers?.forEach((original, trigger) => { trigger.parentElement?.classList.remove('md-dv-host'); trigger.replaceWith(original); });
      this.mobile?.remove(); this.sampleAnchor?.remove();
      this.mobileNav?.remove(); this.originalMobileNav?.classList.remove('md-luxe-mobile-original');
      this.veil?.remove();
      if (this.panel) this.panel.hidden = true;
      if (this.page?.isConnected && this.pageOriginal) this.page.replaceChildren(...this.pageOriginal);
      if (this.dialog?.open) this.dialog.close();
      document.body.classList.remove('md-oracle-open');
      document.body.classList.remove('md-dv-menu-open');
    }
  }
  customElements.define('md-daniverse', Daniverse);
})();
